import { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    RadioGroup,
    List,
    ListItem,
    ListItemButton,
    FormControlLabel,
    Radio,
    CircularProgress,
    Modal,
    Box,
} from "@mui/material";

import { create, InstanceProps } from "react-modal-promise";
import { IQuote } from "src/Interfaces/quotes";
import { ICountryData, ISetCustomerData } from "@interfaces";
import moment from "moment";
import { Form, Formik, FormikHelpers } from "formik";
import { AddTravellerFormComponent } from "src/Components/AddTravellerComponent/AddTravellerFormComponent";
import _ from "lodash";
import { StyledButton } from "@components";
import { createNewCartCustomerSchema } from "@validations";
import { BOOKING_DATE_FORMAT } from "@constants";
import styled from "styled-components";
import { DiscountInfoModal } from "./DiscountInfoModal";
import { breakpoint, theme } from "@utilities";
import { useAppSelector } from "@redux";
import { selectQuotes } from "src/Redux/Slices/allQuotes";

interface IConfirmModalProps extends InstanceProps<boolean, boolean> {
    isOpen: boolean;
    message: string;
    onResolve: (confirmed?: boolean) => void;
    onReject: () => void;
}

export const ConfirmModal = ({ isOpen, message, onResolve }: IConfirmModalProps) => {
    return (
        <Dialog open={isOpen} onClose={() => onResolve()}>
            <DialogTitle>Confirm</DialogTitle>
            <DialogContent>{message}</DialogContent>
            <DialogActions>
                <Button onClick={() => onResolve()} color="secondary">
                    Cancel
                </Button>
                <Button onClick={() => onResolve(true)} color="primary">
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const confirmModal = create<IConfirmModalProps>(ConfirmModal);

interface ICreateNewCustomerModalProps extends InstanceProps<ISetCustomerData, string> {
    isOpen: boolean;
    onResolve: (customerData?: ISetCustomerData) => void;
    onReject: () => void;
    loadCountries: () => Promise<ICountryData[]>;
}

export const CreateNewCustomerModal = ({
    isOpen,
    onResolve,
    onReject,
    loadCountries,
}: ICreateNewCustomerModalProps) => {
    const [countriesData, setCountriesData] = useState<ICountryData[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setIsLoading(true);
                const countries = await loadCountries();
                setCountriesData(countries);
            } catch (error) {
                console.error("Failed to load countries:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            fetchCountries();
        }
    }, [isOpen, loadCountries]);

    const initialValues: ISetCustomerData = {
        title: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        postalCode: "",
        countryCode: "",
        dateOfBirth: "",
        customerIndex: 0,
    };

    const handleSubmit = async (
        values: ISetCustomerData,
        { setSubmitting, validateForm, setTouched }: FormikHelpers<ISetCustomerData>,
    ) => {
        try {
            const validationResult = await validateForm();
            if (!_.isEmpty(validationResult)) {
                setTouched({
                    title: true,
                    firstName: true,
                    lastName: true,
                    dateOfBirth: true,
                    phoneNumber: true,
                    email: true,
                    postalCode: true,
                    countryCode: true,
                });
                return;
            }
            setSubmitting(true);
            const dob = moment(values.dateOfBirth);
            const age = moment().diff(dob, "years");
            const formattedValues = {
                ...values,
                dateOfBirth: dob.format(BOOKING_DATE_FORMAT),
                age,
            };
            await onResolve(formattedValues);
        } catch (error) {
            console.error("Submission error:", error);
            onReject();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={isOpen}
            onClose={() => onResolve()}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "auto",
                padding: "16px",
                /* Safari mobile background fixes */
                "& .MuiBackdrop-root": {
                    backgroundColor: "rgba(0, 0, 0, 0.4) !important",
                    WebkitBackdropFilter: "none",
                    backdropFilter: "none",
                },
                "& .MuiDialog-paper": {
                    backgroundColor: "#fff !important",
                    WebkitBackgroundColor: "#fff !important",
                    background: "#fff !important",
                    /* Safari mobile specific properties */
                    WebkitAppearance: "none",
                    WebkitBorderRadius: "8px",
                    borderRadius: "8px",
                    /* Ensure proper Safari mobile rendering */
                    WebkitBackfaceVisibility: "hidden",
                    backfaceVisibility: "hidden",
                    WebkitTransform: "translateZ(0)",
                    transform: "translateZ(0)",
                },
                /* Mobile-specific styles */
                "@media (max-width: 768px)": {
                    "& .MuiDialog-paper": {
                        backgroundColor: "#fff !important",
                        WebkitBackgroundColor: "#fff !important",
                        background: "#fff !important",
                        /* Force white background on mobile */
                        WebkitBackgroundClip: "padding-box",
                        backgroundClip: "padding-box",
                        /* Safari mobile appearance fixes */
                        WebkitAppearance: "none",
                        WebkitBorderRadius: "8px",
                        borderRadius: "8px",
                        /* Ensure proper Safari mobile rendering */
                        WebkitBackfaceVisibility: "hidden",
                        backfaceVisibility: "hidden",
                        WebkitTransform: "translateZ(0)",
                        transform: "translateZ(0)",
                        /* Force solid background */
                        WebkitBoxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    },
                },
                /* Safari-specific mobile fixes */
                "@supports (-webkit-touch-callout: none)": {
                    "@media (max-width: 768px)": {
                        "& .MuiDialog-paper": {
                            backgroundColor: "#fff !important",
                            WebkitBackgroundColor: "#fff !important",
                            background: "#fff !important",
                            /* Safari mobile specific properties */
                            WebkitAppearance: "none",
                            WebkitBorderRadius: "8px",
                            borderRadius: "8px",
                            /* Ensure proper Safari mobile rendering */
                            WebkitBackfaceVisibility: "hidden",
                            backfaceVisibility: "hidden",
                            WebkitTransform: "translateZ(0)",
                            transform: "translateZ(0)",
                            /* Force solid background in Safari */
                            WebkitBackgroundClip: "padding-box",
                            backgroundClip: "padding-box",
                            WebkitBoxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                        },
                    },
                },
            }}
        >
            {isLoading ? (
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            ) : (
                <Box
                    sx={{
                        width: "90vw",
                        maxWidth: 700,
                        /* Force white background */
                        backgroundColor: "#fff !important",
                        WebkitBackgroundColor: "#fff !important",
                        background: "#fff !important",
                        /* Safari mobile specific properties */
                        WebkitAppearance: "none",
                        WebkitBorderRadius: "8px",
                        borderRadius: "8px",
                        /* Ensure proper Safari mobile rendering */
                        WebkitBackfaceVisibility: "hidden",
                        backfaceVisibility: "hidden",
                        WebkitTransform: "translateZ(0)",
                        transform: "translateZ(0)",
                        /* Force solid background */
                        WebkitBackgroundClip: "padding-box",
                        backgroundClip: "padding-box",
                        /* Mobile-specific styles */
                        "@media (max-width: 768px)": {
                            backgroundColor: "#fff !important",
                            WebkitBackgroundColor: "#fff !important",
                            background: "#fff !important",
                            /* Force white background on mobile */
                            WebkitBackgroundClip: "padding-box",
                            backgroundClip: "padding-box",
                            /* Safari mobile appearance fixes */
                            WebkitAppearance: "none",
                            WebkitBorderRadius: "8px",
                            borderRadius: "8px",
                            /* Ensure proper Safari mobile rendering */
                            WebkitBackfaceVisibility: "hidden",
                            backfaceVisibility: "hidden",
                            WebkitTransform: "translateZ(0)",
                            transform: "translateZ(0)",
                            /* Force solid background */
                            WebkitBoxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                        },
                        /* Safari-specific mobile fixes */
                        "@supports (-webkit-touch-callout: none)": {
                            "@media (max-width: 768px)": {
                                backgroundColor: "#fff !important",
                                WebkitBackgroundColor: "#fff !important",
                                background: "#fff !important",
                                /* Safari mobile specific properties */
                                WebkitAppearance: "none",
                                WebkitBorderRadius: "8px",
                                borderRadius: "8px",
                                /* Ensure proper Safari mobile rendering */
                                WebkitBackfaceVisibility: "hidden",
                                backfaceVisibility: "hidden",
                                WebkitTransform: "translateZ(0)",
                                transform: "translateZ(0)",
                                /* Force solid background in Safari */
                                WebkitBackgroundClip: "padding-box",
                                backgroundClip: "padding-box",
                                WebkitBoxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                            },
                        },
                    }}
                >
                    <Formik
                        initialValues={initialValues}
                        validationSchema={createNewCartCustomerSchema}
                        onSubmit={handleSubmit}
                    >
                        {({
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            isSubmitting,
                            isValid,
                        }) => (
                            <StyledForm>
                                <Header>
                                    <p className="heading">New Customer</p>
                                </Header>
                                <AddTravellerFormComponent
                                    values={values}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    handleSubmit={handleSubmit}
                                    dateValue={
                                        values.dateOfBirth
                                            ? moment(values.dateOfBirth).toDate()
                                            : null
                                    }
                                    countriesData={countriesData}
                                    handleChangeDate={(date) =>
                                        setFieldValue(
                                            "dateOfBirth",
                                            date ? moment(date).format("YYYY-MM-DD") : "",
                                        )
                                    }
                                    isShowErrorDate={false}
                                />
                                <StyledButton
                                    className="saveButton"
                                    color={theme.colors.main}
                                    $borderRadius={theme.borderRadius.small}
                                    type="submit"
                                    disabled={!isValid || isSubmitting}
                                >
                                    {isSubmitting ? "Saving ..." : "Save"}
                                </StyledButton>
                            </StyledForm>
                        )}
                    </Formik>
                </Box>
            )}
        </Modal>
    );
};

export const createNewCustomerModal = create<ICreateNewCustomerModalProps>(CreateNewCustomerModal);

interface ICreateNewQuoteModalProps extends InstanceProps<string, string> {
    isOpen: boolean;
    onResolve: (title?: string) => void;
    onReject: () => void;
}

export const CreateNewQuoteModal = ({ isOpen, onResolve }: ICreateNewQuoteModalProps) => {
    const [quoteTitle, setQuoteTitle] = useState("");

    return (
        <Dialog open={isOpen} onClose={() => onResolve()}>
            <DialogTitle>Create New Quote</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Quote Title"
                    fullWidth
                    variant="outlined"
                    value={quoteTitle}
                    onChange={(e) => setQuoteTitle(e.target.value)}
                    slotProps={{
                        inputLabel: {
                            sx: {
                                color: theme.colors.label,
                                "&.Mui-focused": {
                                    color: theme.colors.label,
                                },
                                "&.MuiInputLabel-shrink": {
                                    color: theme.colors.label,
                                },
                            },
                        },
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onResolve()} color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={() => onResolve(quoteTitle)}
                    color="primary"
                    disabled={!quoteTitle.trim()}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const createNewQuoteModal = create<ICreateNewQuoteModalProps>(CreateNewQuoteModal);

interface IAddToExistingQuoteModalProps extends InstanceProps<IQuote, void> {
    isOpen: boolean;
    onResolve: (quote?: IQuote) => void;
    onReject: () => void;
}

export const AddToExistingQuoteModal = ({
    isOpen,
    onResolve,
    onReject,
}: IAddToExistingQuoteModalProps) => {
    const [selectedQuote, setSelectedQuote] = useState<IQuote | null>(null);
    const { quotes } = useAppSelector(selectQuotes);

    return (
        <Dialog open={isOpen} onClose={() => onResolve()}>
            <DialogTitle>Select an Existing Quote</DialogTitle>
            <DialogContent>
                {quotes.length === 0 ? (
                    <Box sx={{ padding: 2, textAlign: "center", color: "#888" }}>
                        No quotes available
                    </Box>
                ) : (
                    <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                            value={selectedQuote?.id || ""}
                            onChange={(e) => {
                                const quote = quotes.find(
                                    (q) => q.id.toString() === e.target.value,
                                );
                                setSelectedQuote(quote || null);
                            }}
                        >
                            <List>
                                {quotes.map((quote: IQuote) => (
                                    <ListItem key={quote.id} disablePadding>
                                        <ListItemButton
                                            onClick={() => setSelectedQuote(quote)}
                                            sx={{
                                                border:
                                                    selectedQuote?.id === quote.id
                                                        ? `"2px solid ${theme.colors.button_text}"`
                                                        : "1px solid #e0e0e0",
                                                borderRadius: 2,
                                                mb: 1,
                                            }}
                                        >
                                            <FormControlLabel
                                                value={quote.id.toString()}
                                                control={
                                                    <Radio
                                                        sx={{
                                                            color: theme.colors.time_radio,
                                                            "&.Mui-checked": {
                                                                color: theme.colors.time_radio,
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={quote.title}
                                                sx={{ width: "100%", ml: 1 }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </RadioGroup>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onReject} color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={() => onResolve(selectedQuote!)}
                    color="primary"
                    disabled={!selectedQuote}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const addToExistingQuoteModal = create(AddToExistingQuoteModal);

interface IDiscountInfoModalProps extends InstanceProps<void, void> {
    isOpen: boolean;
    percent: number;
    learnMoreHref: string;
    imageUrl?: string;
    onResolve: () => void;
    onReject: () => void;
}

export const DiscountInfoModalPromise = create<IDiscountInfoModalProps>((props) => (
    <DiscountInfoModal
        isOpen={props.isOpen}
        onClose={props.onResolve}
        percent={props.percent}
        learnMoreHref={props.learnMoreHref}
        imageUrl={props.imageUrl}
    />
));

const StyledForm = styled(Form)`
    padding: 30px 16px;
    background-color: #fff;
    border-radius: 8px;

    ${breakpoint.breakOnlyMobile`
        max-height: 80vh;
        overflow-y: auto;
    `}
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    .heading {
        font-size: 28px;
        font-weight: 700;
    }
`;
